// Library — cross-brand asset index, grouped into collapsible rows by
// type (Research / Briefs / Funnels). Brand filter narrows everything.
// Click any card to drill into its dedicated viewer.

import { useEffect, useMemo, useState } from 'react';
import { PALETTE } from '../palette.js';
import { Card, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { api } from '../api.js';

const TYPE_META = {
  'brand-research': {
    label: 'Research', accent: PALETTE.accent,
    title: 'Brand research',
    sub: 'Public-facing profile of each brand',
  },
  'brief': {
    label: 'Brief', accent: PALETTE.accentDeep,
    title: 'Marketing briefs',
    sub: 'Campaign blueprints — funnel, ads, emails, KPIs',
  },
  'funnel': {
    label: 'Funnel', accent: PALETTE.success,
    title: 'Funnels',
    sub: 'Preserved custom lead-site source',
  },
  'scripts': {
    label: 'Scripts', accent: PALETTE.accentDeep,
    title: 'Ad scripts',
    sub: 'Selected campaign messages ready for production',
  },
  'video': {
    label: 'Video', accent: PALETTE.success,
    title: 'Campaign videos',
    sub: 'Generated video and production metadata',
  },
  'review': {
    label: 'Review', accent: PALETTE.accent,
    title: 'Review bundles',
    sub: 'Campaign packages assembled for approval',
  },
  'artifact': {
    label: 'Asset', accent: PALETTE.ink2,
    title: 'Other assets',
    sub: 'Additional campaign output owned by this company',
  },
};

// Order the rows render in. Anything not in this list appears after.
const ROW_ORDER = ['brief', 'funnel', 'scripts', 'video', 'review', 'brand-research', 'artifact'];

function activeCompanyId(session) {
  const memberships = session?.user?.memberships || [];
  return session?.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function artifactSubtitle(artifact) {
  return [
    artifact.campaign?.offer,
    artifact.campaign?.projectType,
    artifact.workflow?.status?.replace(/_/g, ' '),
  ].filter(Boolean).join(' · ');
}

export function normalizeSaasLibraryResponse(response = {}) {
  const assets = (Array.isArray(response.artifacts) ? response.artifacts : []).map((artifact) => ({
    id: artifact.id,
    type: artifact.category || 'artifact',
    title: artifact.title || artifact.label || 'Campaign asset',
    subtitle: artifactSubtitle(artifact),
    brand: artifact.brand?.slug || artifact.brand?.id || '',
    brandName: artifact.brand?.name || 'Company asset',
    brandWebsite: '',
    updatedAt: artifact.createdAt ? Date.parse(artifact.createdAt) : 0,
    action: artifact.action || null,
  }));
  const brands = [...new Map(assets
    .filter((asset) => asset.brand)
    .map((asset) => [asset.brand, {
      slug: asset.brand,
      name: asset.brandName,
    }])).values()];
  return { assets, brands, pagination: response.pagination || null };
}

function mergeLibraryData(current, next) {
  if (!current) return next;
  const assets = [...new Map([...current.assets, ...next.assets].map((asset) => [asset.id, asset])).values()];
  const brands = [...new Map([...current.brands, ...next.brands].map((brand) => [brand.slug, brand])).values()];
  return { ...next, assets, brands };
}

export default function Library({ navigate, session }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [activeBrand, setActiveBrand] = useState('');
  const [openRow, setOpenRow] = useState(undefined);
  const [loadingMore, setLoadingMore] = useState(false);
  const companyId = activeCompanyId(session);
  const isSaasSession = session?.user?.auth?.type === 'supabase';

  useEffect(() => {
    let active = true;
    setData(null);
    setError('');
    setActiveBrand('');
    setOpenRow(undefined);
    const request = isSaasSession
      ? api.get('/api/saas/artifacts?limit=100&offset=0', tenantHeaders(companyId))
        .then(normalizeSaasLibraryResponse)
      : api.get('/api/library');
    request
      .then((response) => { if (active) setData(response); })
      .catch((err) => { if (active) setError(err.message); });
    return () => { active = false; };
  }, [isSaasSession, companyId]);

  // Group filtered assets by type. Brand filter applies before grouping.
  const groups = useMemo(() => {
    const all = (data?.assets || []).filter((a) => !activeBrand || a.brand === activeBrand);
    const byType = {};
    for (const a of all) {
      if (!byType[a.type]) byType[a.type] = [];
      byType[a.type].push(a);
    }
    const ordered = [];
    for (const t of ROW_ORDER) if (byType[t]) ordered.push({ type: t, assets: byType[t] });
    for (const t of Object.keys(byType)) if (!ROW_ORDER.includes(t)) ordered.push({ type: t, assets: byType[t] });
    return ordered;
  }, [data, activeBrand]);

  const filteredCount = groups.reduce((n, g) => n + g.assets.length, 0);

  useEffect(() => {
    if (openRow === undefined && groups.length) setOpenRow(groups[0].type);
  }, [groups, openRow]);

  async function loadMore() {
    if (!isSaasSession || !data?.pagination?.hasMore || loadingMore) return;
    setLoadingMore(true);
    setError('');
    try {
      const response = await api.get(
        `/api/saas/artifacts?limit=100&offset=${data.pagination.nextOffset}`,
        tenantHeaders(companyId),
      );
      setData((current) => mergeLibraryData(current, normalizeSaasLibraryResponse(response)));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div className="library-page" style={{ padding: '32px 40px 64px', maxWidth: 1280, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      <style>{libraryResponsiveStyles}</style>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 24, flexWrap: 'wrap' }}>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 40, fontWeight: 400, margin: 0, color: PALETTE.ink, letterSpacing: '-0.02em',
        }}>Library</h1>
        <Pill tone="accent">Workspace</Pill>
        <div style={{ flex: 1 }} />
        {data && (
          <span style={{ fontSize: 12.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace' }}>
            {filteredCount} item{filteredCount === 1 ? '' : 's'}
          </span>
        )}
      </header>

      {error && <ErrorBox msg={error} />}

      {/* Brand filter */}
      {data && (data.brands?.length || 0) > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <Eyebrow>Filter by brand</Eyebrow>
          <select
            value={activeBrand}
            onChange={(e) => setActiveBrand(e.target.value)}
            style={{
              padding: '6px 10px', fontSize: 12.5, fontFamily: 'Inter, sans-serif',
              color: PALETTE.ink, background: PALETTE.surface,
              border: `1px solid ${PALETTE.line}`, borderRadius: 999, outline: 'none',
              cursor: 'pointer',
            }}
          >
            <option value="">All brands</option>
            {(data.brands || []).map((b) => (
              <option key={b.slug} value={b.slug}>{b.name}</option>
            ))}
          </select>
        </div>
      )}

      {/* Groups */}
      {!data ? (
        error ? null : <ProtectedPageLoader />
      ) : filteredCount === 0 ? (
        <EmptyState empty={!data?.assets?.length} navigate={navigate} isSaasSession={isSaasSession} />
      ) : (
        <>
        {groups.map((g) => {
          const meta = TYPE_META[g.type] || { label: g.type, title: g.type, sub: '' };
          const isOpen = openRow === g.type;
          return (
            <DrillSection
              key={g.type}
              eyebrow={meta.label.toUpperCase()}
              title={meta.title}
              sub={meta.sub}
              count={g.assets.length}
              open={isOpen}
              onToggle={() => setOpenRow(isOpen ? null : g.type)}
            >
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(280px, 100%), 1fr))', gap: 14,
              }}>
                {g.assets.map((a) => (
                  <AssetCard
                    key={a.id || `${a.brand}-${a.type}-${a.title}`}
                    asset={a}
                    navigate={navigate}
                    companyId={companyId}
                    onError={(message) => setError(message)}
                  />
                ))}
              </div>
            </DrillSection>
          );
        })}
        {data?.pagination?.hasMore && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            <button type="button" onClick={loadMore} disabled={loadingMore} style={ghostBtn}>
              {loadingMore ? 'Loading more...' : 'Load more'}
            </button>
          </div>
        )}
        </>
      )}
    </div>
  );
}

// ─── DRILL SECTION (matches BrandResearch pattern) ────────────────────────
export function drillSectionFrameStyle(open) {
  const sideColor = open ? PALETTE.line : 'transparent';
  return {
    background: open ? PALETTE.surface : 'transparent',
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderLeftWidth: 1,
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: PALETTE.line,
    borderRightColor: sideColor,
    borderBottomColor: sideColor,
    borderLeftColor: sideColor,
    borderRadius: open ? 12 : 0,
    marginBottom: open ? 14 : 0,
    overflow: 'hidden',
  };
}

function DrillSection({ eyebrow, title, sub, count, open, onToggle, children }) {
  return (
    <div style={drillSectionFrameStyle(open)}>
      <button className="library-section-toggle" onClick={onToggle} style={{
        display: 'flex', alignItems: 'center', gap: 14, width: '100%',
        padding: open ? '16px 20px 8px' : '16px 6px',
        background: 'transparent', border: 'none', cursor: 'pointer',
        textAlign: 'left', fontFamily: 'Inter, sans-serif',
      }}>
        <Eyebrow>{eyebrow}</Eyebrow>
        <div className="library-section-title" style={{
          fontFamily: 'Instrument Serif, serif', fontSize: 22,
          color: PALETTE.ink, lineHeight: 1, letterSpacing: '-0.005em',
        }}>{title}</div>
        {sub && <div className="library-section-sub" style={{ fontSize: 12, color: PALETTE.ink3 }}>{sub}</div>}
        {count !== undefined && (
          <span style={{
            padding: '2px 8px', fontSize: 11, fontFamily: 'JetBrains Mono, monospace',
            background: PALETTE.surfaceAlt, color: PALETTE.ink2,
            border: `1px solid ${PALETTE.line}`, borderRadius: 999,
          }}>{count}</span>
        )}
        <div className="library-section-spacer" style={{ flex: 1 }} />
        <span className="library-section-action" style={{
          fontSize: 11, color: PALETTE.ink3, fontFamily: 'Inter, sans-serif',
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          <span className="library-section-action-label">{open ? 'Collapse' : 'Expand'}</span>
          <span style={{
            display: 'inline-block',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform .15s',
          }}>›</span>
        </span>
      </button>
      {open && <div className="library-section-content" style={{ padding: '8px 20px 20px' }}>{children}</div>}
    </div>
  );
}

// ─── ASSET CARD ───────────────────────────────────────────────────────────
function AssetCard({ asset, navigate, companyId, onError }) {
  const meta = TYPE_META[asset.type] || { label: asset.type, accent: PALETTE.ink2 };
  const initial = (asset.brandName || asset.title || '?').trim().charAt(0).toUpperCase();
  const domain = (asset.brandWebsite || '').replace(/^https?:\/\//, '').split('/')[0];
  const fav = domain ? `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=32` : '';

  async function go() {
    if (asset.action?.type === 'internal_route' && asset.action.route) {
      navigate(asset.action.route);
      return;
    }
    if (asset.action?.type === 'signed_preview' && asset.action.artifactId) {
      try {
        const result = await api.get(
          `/api/saas/artifacts/${encodeURIComponent(asset.action.artifactId)}/signed-url`,
          tenantHeaders(companyId),
        );
        const signedUrl = result?.signedUrl?.url;
        const parsed = new URL(signedUrl);
        if (!['http:', 'https:'].includes(parsed.protocol)) throw new Error('Preview URL is unavailable');
        window.location.assign(parsed.href);
      } catch (err) {
        onError?.(err?.safeMessage || 'Asset preview is unavailable');
      }
      return;
    }
    // Translate legacy /<page>.html?... links to React hash routes.
    const href = asset.href || '';
    if (href.includes('brand-research.html')) {
      const brand = new URL(href, 'http://x').searchParams.get('brand');
      navigate(`brand-research?brand=${encodeURIComponent(brand)}`);
      return;
    }
    if (href.includes('marketing-agent.html')) {
      const u = new URL(href, 'http://x');
      navigate(`marketing-brief?brand=${encodeURIComponent(u.searchParams.get('brand'))}&brief=${encodeURIComponent(u.searchParams.get('brief'))}`);
      return;
    }
    if (href.includes('funnels.html')) {
      const u = new URL(href, 'http://x');
      navigate(`funnels?brand=${encodeURIComponent(u.searchParams.get('brand'))}&funnel=${encodeURIComponent(u.searchParams.get('funnel'))}`);
      return;
    }
    window.location.href = href;
  }

  const unavailable = asset.action?.type === 'unavailable';

  return (
    <button
      onClick={unavailable ? undefined : go}
      disabled={unavailable}
      aria-disabled={unavailable}
      title={unavailable ? 'This asset does not have enough tenant context to open safely.' : undefined}
      style={{
      textAlign: 'left', padding: 0, cursor: unavailable ? 'not-allowed' : 'pointer', fontFamily: 'Inter, sans-serif',
      background: 'transparent', border: 'none', minWidth: 0,
      opacity: unavailable ? 0.72 : 1,
    }}>
      <div style={{
        background: PALETTE.surface, border: `1px solid ${PALETTE.line}`, borderRadius: 14,
        padding: 18, display: 'flex', flexDirection: 'column', gap: 10,
        height: '100%', minHeight: 168,
        transition: 'border-color .15s, box-shadow .15s',
      }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = PALETTE.accentSoft; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = PALETTE.line; }}
      >
        {/* Top: type pill + brand monogram */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 10px', fontSize: 10.5, fontWeight: 500,
            letterSpacing: '0.08em', textTransform: 'uppercase',
            color: meta.accent, background: PALETTE.surfaceAlt,
            border: `1px solid ${PALETTE.line}`, borderRadius: 999,
            fontFamily: 'JetBrains Mono, monospace',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: 999, background: meta.accent }} />
            {meta.label}
          </span>
          <BrandMonogram initial={initial} />
        </div>

        {/* Title + subtitle */}
        <div style={{ flex: 1, minHeight: 0 }}>
          <div style={{
            fontFamily: 'Instrument Serif, serif', fontSize: 22, fontWeight: 400,
            color: PALETTE.ink, lineHeight: 1.2, letterSpacing: '-0.01em',
            marginBottom: 6,
            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>{asset.title}</div>
          {asset.subtitle && (
            <div style={{
              fontSize: 12.5, color: PALETTE.ink3, lineHeight: 1.45,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}>{asset.subtitle}</div>
          )}
        </div>

        {/* Foot: brand row + relative time */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          paddingTop: 10, borderTop: `1px solid ${PALETTE.lineSoft}`,
          fontSize: 11.5, color: PALETTE.ink3,
        }}>
          {fav && (
            <img src={fav} alt="" style={{ width: 14, height: 14, borderRadius: 3 }}
              onError={(e) => { e.currentTarget.style.display = 'none'; }} />
          )}
          <span style={{
            flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            color: PALETTE.ink2,
          }}>{asset.brandName}</span>
          <span style={{ fontFamily: 'JetBrains Mono, monospace' }}>{relTime(asset.updatedAt)}</span>
        </div>
      </div>
    </button>
  );
}

function BrandMonogram({ initial }) {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: 7,
      background: PALETTE.ink, color: PALETTE.accent,
      display: 'grid', placeItems: 'center',
      fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
      fontSize: 16, letterSpacing: '-0.02em',
    }}>{initial}</div>
  );
}

function relTime(ms) {
  if (!ms) return '';
  const s = Math.floor((Date.now() - ms) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  const d = Math.floor(s / 86400);
  if (d < 7) return `${d}d`;
  if (d < 30) return `${Math.floor(d / 7)}w`;
  return new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// ─── FILTER CHIP ──────────────────────────────────────────────────────────
function FilterChip({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '6px 12px', borderRadius: 999, cursor: 'pointer',
      fontFamily: 'Inter, sans-serif', fontSize: 12.5,
      fontWeight: active ? 500 : 400,
      background: active ? PALETTE.surface : 'transparent',
      color: active ? PALETTE.ink : PALETTE.ink2,
      border: `1px solid ${active ? PALETTE.accent : PALETTE.line}`,
      boxShadow: active ? `0 0 0 2px ${PALETTE.accentTint}` : 'none',
      transition: 'all .15s',
    }}>{children}</button>
  );
}
function Count({ children }) {
  return (
    <span style={{
      fontSize: 10.5, color: PALETTE.ink3, fontFamily: 'JetBrains Mono, monospace',
      padding: '0 4px',
    }}>{children}</span>
  );
}

// ─── EMPTY STATE ──────────────────────────────────────────────────────────
function EmptyState({ empty, navigate, isSaasSession }) {
  if (empty) {
    return (
      <Card style={{ padding: 40, textAlign: 'center' }}>
        <div style={{
          width: 56, height: 56, borderRadius: '50%', margin: '0 auto 16px',
          background: PALETTE.accentTint, color: PALETTE.accentDeep,
          display: 'grid', placeItems: 'center',
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 28,
        }}>L</div>
        <h2 style={{
          fontFamily: 'Instrument Serif, serif', fontStyle: 'italic',
          fontSize: 26, fontWeight: 400, margin: '0 0 6px', color: PALETTE.ink,
        }}>Your library is empty</h2>
        <p style={{ color: PALETTE.ink2, fontSize: 14, lineHeight: 1.5, margin: '0 0 20px' }}>
          {isSaasSession
            ? 'Campaign outputs will appear here as your company completes workflows.'
            : 'Generate brand research or build a marketing brief to fill it up.'}
        </p>
        <div style={{ display: 'inline-flex', gap: 8 }}>
          {!isSaasSession && (
            <button onClick={() => navigate('brand-research')} style={inkBtn}>Run brand research</button>
          )}
          <button onClick={() => navigate('wizard')} style={isSaasSession ? inkBtn : ghostBtn}>
            Start campaign
          </button>
        </div>
      </Card>
    );
  }
  return (
    <Card style={{ padding: 32, textAlign: 'center' }}>
      <div style={{ fontSize: 14, color: PALETTE.ink2 }}>
        No matches. Try a different filter.
      </div>
    </Card>
  );
}

function ErrorBox({ msg }) {
  return (
    <div style={{
      background: PALETTE.dangerSoft, border: '1px solid #E8C9C1', color: '#8B2E1A',
      borderRadius: 12, padding: '12px 16px', fontSize: 13, marginBottom: 20,
    }}>{msg}</div>
  );
}

function Eyebrow({ children, style = {} }) {
  return (
    <div style={{
      fontFamily: 'JetBrains Mono, monospace',
      fontSize: 10.5, letterSpacing: '0.14em', textTransform: 'uppercase',
      color: PALETTE.accent, fontWeight: 500,
      ...style,
    }}>{children}</div>
  );
}

const inkBtn = {
  padding: '10px 16px', fontSize: 13, fontWeight: 500,
  background: PALETTE.ink, color: '#FAF8F5',
  border: `1px solid ${PALETTE.ink}`, borderRadius: 9, cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};
const ghostBtn = {
  padding: '10px 16px', fontSize: 13, fontWeight: 500,
  background: 'transparent', color: PALETTE.ink,
  border: `1px solid ${PALETTE.line}`, borderRadius: 9, cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
};

const libraryResponsiveStyles = `
  @media (max-width: 640px) {
    .library-page {
      padding: 28px 16px 48px !important;
    }
    .library-section-toggle {
      display: grid !important;
      grid-template-columns: auto minmax(0, 1fr) auto auto;
      gap: 8px !important;
      padding: 14px 8px !important;
    }
    .library-section-title {
      min-width: 0;
    }
    .library-section-sub,
    .library-section-action-label,
    .library-section-spacer {
      display: none !important;
    }
    .library-section-action {
      justify-self: end;
    }
    .library-section-content {
      padding: 4px 12px 16px !important;
    }
  }
`;
